import { useEffect, useRef, type KeyboardEvent } from 'react';
import { imageToScreen } from './markupGeometry';
import type { TextDraft } from './markupDrafts';
import { TEXT_FONT_FAMILY, TEXT_FONT_WEIGHT } from './markupStyle';
import { measureLabel } from './textMeasure';

interface MarkupTextFieldProps {
    draft: TextDraft;
    scale: number;
    onChange: (value: string) => void;
    onCommit: () => void;
    onCancel: () => void;
}

const PLACEHOLDER = 'Type a label';
// Room for the caret after the last glyph, as a share of the font size.
const CARET_ROOM_EM = 0.5;

// Styled like the painted label, so committing barely moves the text.
function MarkupTextField({ draft, scale, onChange, onCommit, onCancel }: MarkupTextFieldProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const position = imageToScreen(draft.at, scale);
    const fontSize = draft.fontSize * scale;
    const width =
        measureLabel(draft.value || PLACEHOLDER, draft.fontSize) * scale + fontSize * CARET_ROOM_EM;

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        input.select();
    }, []);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            onCommit();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
        }
    };

    return (
        <input
            ref={inputRef}
            value={draft.value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={onCommit}
            aria-label="Label text"
            placeholder={PLACEHOLDER}
            className="absolute bg-transparent p-0 m-0 border-0 rounded-sm outline-dashed outline-1 outline-offset-4 outline-blue-500 placeholder:text-current placeholder:opacity-50"
            style={{
                left: position.x,
                top: position.y,
                width,
                fontSize,
                lineHeight: 1,
                fontFamily: TEXT_FONT_FAMILY,
                fontWeight: TEXT_FONT_WEIGHT,
                color: draft.color
            }}
        />
    );
}

export { MarkupTextField };
